import {
  BinaryWriter,
  FrameFlag,
  Opcode,
  PROTOCOL_VERSION,
  StatusCode,
  TlvTag,
  decodeTlvs,
  encodeTlvs,
  tlv,
  type ProtocolMessage,
  type Tlv,
} from "./protocol";
import {
  DEFAULT_SETTINGS,
  type CommandOutcome,
  type CommandResultCode,
  type DeviceState,
  type HeadId,
  type HeadPhase,
  type HeadStatus,
  type HeadState,
  type SettingDescriptor,
  type SnapshotSummary,
  type Trace,
} from "./model";
import type { RemoteApi, RemoteEvent } from "./remote-api";
import type { BleTransport } from "./transport";
import {
  AccessCredentialStore,
  ApplicationSession,
  EnrollmentRequiredError,
  InvalidAccessKeyError,
  decodeAccessCode,
} from "./auth";

const SETTING_COUNT = DEFAULT_SETTINGS.length;

interface ResponseEnvelope {
  requestId: number;
  status: StatusCode;
  tlvs: Tlv[];
}

interface PendingRequest {
  opcode: Opcode;
  resolve: (response: ResponseEnvelope) => void;
  reject: (error: Error) => void;
  timeout?: number;
}

class CommandChannel {
  readonly #transport: BleTransport;
  #nextRequestId = 1;
  #pending = new Map<number, PendingRequest>();
  #eventListeners = new Set<(message: ProtocolMessage) => void>();
  #offMessage: () => void;
  #offDisconnect: () => void;
  #session?: ApplicationSession;
  #receiveQueue: Promise<void> = Promise.resolve();
  #transmitQueue: Promise<void> = Promise.resolve();

  constructor(transport: BleTransport) {
    this.#transport = transport;
    this.#offMessage = transport.onMessage((message) => {
      this.#receiveQueue = this.#receiveQueue.then(
        () => this.#receive(message),
        () => this.#receive(message),
      );
    });
    this.#offDisconnect = transport.onDisconnect(() => {
      this.#session = undefined;
      this.#rejectAll(new Error("Controller disconnected before the command result arrived."));
    });
  }

  setSession(session?: ApplicationSession): void {
    this.#session = session;
  }

  request(opcode: Opcode, tlvs: readonly Tlv[] = []): Promise<ResponseEnvelope> {
    let requestId = this.#nextRequestId;
    do {
      requestId = this.#nextRequestId;
      this.#nextRequestId = this.#nextRequestId >= 0xffff ? 1 : this.#nextRequestId + 1;
    } while (this.#pending.has(requestId));

    let request!: PendingRequest;
    const response = new Promise<ResponseEnvelope>((resolve, reject) => {
      request = { opcode, resolve, reject };
      this.#pending.set(requestId, request);
    });
    const transmit = this.#transmitQueue.then(async () => {
      // A disconnect may reject a request while it is waiting behind another
      // fragmented write. Never send that stale request on a later session.
      if (this.#pending.get(requestId) !== request) return;
      const logical = { opcode, flags: FrameFlag.Request, requestId, payload: encodeTlvs(tlvs) };
      const outgoing = this.#session && opcode !== Opcode.GetAuthChallenge && opcode !== Opcode.Authenticate
        ? await this.#session.protect(logical)
        : logical;
      // WebCrypto yields. The link can disconnect and a new GATT connection
      // can arrive while the HMAC is being computed, so validate request
      // identity again before an old-session packet reaches the new link.
      if (this.#pending.get(requestId) !== request) return;
      await this.#transport.send(outgoing);
      // The response deadline starts only after every request fragment has
      // been written. If a fast response arrived during send(), it already
      // removed this exact pending object and no orphan timer is created.
      if (this.#pending.get(requestId) === request) {
        request.timeout = window.setTimeout(() => {
          this.#pending.delete(requestId);
          request.reject(new Error("The controller did not respond. Reconnect to verify its state."));
        }, 8_000);
      }
    });
    this.#transmitQueue = transmit.catch(() => undefined);
    void transmit.catch((error) => {
      const pending = this.#pending.get(requestId);
      if (!pending) return;
      if (pending.timeout !== undefined) window.clearTimeout(pending.timeout);
      this.#pending.delete(requestId);
      pending.reject(error instanceof Error ? error : new Error("Could not send controller command."));
    });
    return response;
  }

  onEvent(listener: (message: ProtocolMessage) => void): () => void {
    this.#eventListeners.add(listener);
    return () => this.#eventListeners.delete(listener);
  }

  destroy(): void {
    this.#offMessage();
    this.#offDisconnect();
    this.#rejectAll(new Error("Remote client closed."));
  }

  async #receive(received: ProtocolMessage): Promise<void> {
    let message = received;
    if (this.#session && message.opcode !== Opcode.GetAuthChallenge && message.opcode !== Opcode.Authenticate) {
      try {
        message = await this.#session.open(message);
      } catch (error) {
        const pending = this.#pending.get(message.requestId);
        if (pending) {
          if (pending.timeout !== undefined) window.clearTimeout(pending.timeout);
          this.#pending.delete(message.requestId);
          pending.reject(error instanceof Error ? error : new Error("Controller message authentication failed."));
        }
        return;
      }
    }
    if (message.flags === FrameFlag.Event) {
      for (const listener of this.#eventListeners) listener(message);
      return;
    }
    if (message.flags !== FrameFlag.Response || message.payload.length < 1) return;
    const pending = this.#pending.get(message.requestId);
    if (!pending) return;
    if (pending.timeout !== undefined) window.clearTimeout(pending.timeout);
    this.#pending.delete(message.requestId);
    if (pending.opcode !== message.opcode) {
      pending.reject(new Error("Controller response opcode did not match its request."));
      return;
    }
    try {
      pending.resolve({ requestId: message.requestId, status: message.payload[0] as StatusCode, tlvs: decodeTlvs(message.payload.slice(1)) });
    } catch (error) {
      pending.reject(error instanceof Error ? error : new Error("Malformed controller response."));
    }
  }

  #rejectAll(error: Error): void {
    for (const pending of this.#pending.values()) {
      if (pending.timeout !== undefined) window.clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.#pending.clear();
  }
}

const STATUS_CODES: Record<StatusCode, CommandResultCode> = {
  [StatusCode.Ok]: "ok",
  [StatusCode.Queued]: "queued",
  [StatusCode.Busy]: "busy",
  [StatusCode.Invalid]: "invalid",
  [StatusCode.NotFound]: "not_found",
  [StatusCode.Conflict]: "conflict",
  [StatusCode.StorageUnavailable]: "storage_unavailable",
  [StatusCode.ConfirmationRequired]: "confirmation_required",
  [StatusCode.PersistencePending]: "persistence_pending",
  [StatusCode.Unauthorized]: "unauthorized",
};

const CLASSIFICATIONS = ["NONE", "SHOT", "FLUSH", "INCOMPLETE", "SENSOR FAULT"] as const;

function exact(tlvs: readonly Tlv[], tag: number, length?: number): Uint8Array | undefined {
  const matches = tlvs.filter((item) => item.tag === tag);
  if (matches.length > 1) throw new Error(`Duplicate TLV 0x${tag.toString(16)}`);
  const value = matches[0]?.value;
  if (value && length !== undefined && value.length !== length) throw new Error(`Invalid TLV 0x${tag.toString(16)} length`);
  return value;
}

function required(tlvs: readonly Tlv[], tag: number, length?: number): Uint8Array {
  const value = exact(tlvs, tag, length);
  if (!value) throw new Error(`Missing TLV 0x${tag.toString(16)}`);
  return value;
}

function readU8(value?: Uint8Array, fallback = 0): number {
  if (!value) return fallback;
  if (value.length !== 1) throw new Error("Expected u8 TLV");
  return value[0] ?? fallback;
}

function readU16(value?: Uint8Array, fallback = 0): number {
  if (!value) return fallback;
  if (value.length !== 2) throw new Error("Expected u16 TLV");
  return new DataView(value.buffer, value.byteOffset, value.byteLength).getUint16(0, true);
}

function readU32(value?: Uint8Array, fallback = 0): number {
  if (!value) return fallback;
  if (value.length !== 4) throw new Error("Expected u32 TLV");
  return new DataView(value.buffer, value.byteOffset, value.byteLength).getUint32(0, true);
}

function readU64(value?: Uint8Array, fallback = 0n): bigint {
  if (!value) return fallback;
  if (value.length !== 8) throw new Error("Expected u64 TLV");
  return new DataView(value.buffer, value.byteOffset, value.byteLength).getBigUint64(0, true);
}

function readPersistenceFailed(tlvs: readonly Tlv[]): boolean {
  const persistence = readU8(required(tlvs, TlvTag.Persistence, 1));
  if (persistence > 2) throw new Error("Controller returned an invalid persistence state.");
  return persistence === 2;
}

function safeCounter(value: bigint): number {
  return Number(value > BigInt(Number.MAX_SAFE_INTEGER) ? BigInt(Number.MAX_SAFE_INTEGER) : value);
}

function phaseFromStatus(status: number, demo = false): HeadPhase {
  if (demo) return "demo";
  if (status === 1 || status === 2) return "prewet";
  if (status === 3) return "extracting";
  if (status >= 4) return "complete";
  return "idle";
}

function statusFromWire(status: number): HeadStatus {
  switch (status) {
    case 1: return "analyzing";
    case 2: return "prewet";
    case 3: return "live";
    case 4: return "flush_ignored";
    case 5: return "incomplete";
    case 6: return "sensor_fault";
    case 7: return "storage_error";
    default: return "last";
  }
}

export function parseHead(value: Uint8Array, previous?: HeadState, savePending = false): HeadState {
  if (value.length !== 20) throw new Error("Invalid head-state record");
  const view = new DataView(value.buffer, value.byteOffset, value.byteLength);
  const wireHead = value[0] ?? 0;
  if (wireHead > 1) throw new Error("Invalid head ID");
  const id = (wireHead + 1) as HeadId;
  const flags = value[3] ?? 0;
  const traceKind = value[2] ?? 0;
  const startingLiveTrace = traceKind === 1 && previous?.wireTraceKind !== undefined && previous.wireTraceKind !== 1;
  return {
    id,
    phase: phaseFromStatus(value[1] ?? 0, Boolean(flags & 0x04)),
    status: statusFromWire(value[1] ?? 0),
    reasonFlags: view.getUint32(12, true),
    detectorActive: Boolean(flags & 0x02),
    demoActive: Boolean(flags & 0x04),
    saveAvailable: Boolean(flags & 0x08),
    volumeMl: view.getFloat32(4, true),
    elapsedMs: view.getUint32(8, true),
    score: flags & 0x01 ? value[16] : undefined,
    trace: traceKind === 0 || startingLiveTrace ? { points: [] } : previous?.trace ?? { points: [] },
    reference: previous?.reference,
    pendingSave: savePending,
    liveSampleIndex: startingLiveTrace ? 0 : previous?.liveSampleIndex,
    liveTraceEpoch: startingLiveTrace ? undefined : previous?.liveTraceEpoch,
    liveTotalTicks: startingLiveTrace ? 0 : previous?.liveTotalTicks,
    liveConnectionResetPending: previous?.liveConnectionResetPending,
    wireTraceKind: traceKind,
  };
}

export function parseSnapshot(value: Uint8Array, selectedId?: number): SnapshotSummary {
  if (value.length < 47) throw new Error("Truncated snapshot record");
  const view = new DataView(value.buffer, value.byteOffset, value.byteLength);
  const nameBytes = value[46] ?? 0;
  if (nameBytes > 24 || value.length !== 47 + nameBytes) throw new Error("Invalid snapshot name length");
  const id = view.getUint32(0, true);
  return {
    id,
    capturedShotNumber: view.getUint32(4, true),
    name: new TextDecoder("utf-8", { fatal: true }).decode(value.slice(47)),
    volumeMl: view.getFloat32(26, true),
    durationMs: Math.round(view.getFloat32(30, true) * 1000),
    sampleCount: view.getUint16(18, true),
    classification: CLASSIFICATIONS[value[20] ?? 0] ?? "UNKNOWN",
    active: selectedId === id,
  };
}

export function parseTrace(metadata: Uint8Array, ticksValue?: Uint8Array): { source: number; trace: Trace; volumeMl: number; elapsedMs: number; score?: number } {
  if (metadata.length !== 88) throw new Error("Invalid trace metadata record");
  if (ticksValue && ticksValue.length % 2 !== 0) throw new Error("Invalid packed trace ticks");
  const view = new DataView(metadata.buffer, metadata.byteOffset, metadata.byteLength);
  const flags = metadata[3] ?? 0;
  const framePeriodMs = view.getUint16(20, true);
  const sampleCount = view.getUint16(22, true);
  const calibration = view.getFloat32(32, true);
  const ticks = ticksValue ?? new Uint8Array();
  if (ticks.length / 2 !== sampleCount) throw new Error("Trace sample count mismatch");
  let cumulativeMl = 0;
  const points = Array.from({ length: sampleCount }, (_, index) => {
    cumulativeMl += new DataView(ticks.buffer, ticks.byteOffset + index * 2, 2).getUint16(0, true) * calibration;
    return { elapsedMs: (index + 1) * framePeriodMs, milliliters: cumulativeMl };
  });
  return {
    source: metadata[0] ?? 0,
    trace: {
      points,
      plateauStartMs: flags & 0x01 ? view.getUint16(24, true) * framePeriodMs : undefined,
      plateauEndMs: flags & 0x01 ? view.getUint16(26, true) * framePeriodMs : undefined,
      classification: CLASSIFICATIONS[metadata[2] ?? 0] ?? "UNKNOWN",
      reasonFlags: view.getUint32(16, true),
    },
    volumeMl: view.getFloat32(36, true),
    elapsedMs: Math.round(view.getFloat32(40, true) * 1000),
    score: flags & 0x02 ? metadata[28] : undefined,
  };
}

export function parseSetting(value: Uint8Array): SettingDescriptor {
  if (value.length !== 24) throw new Error("Invalid setting record");
  const id = value[0] ?? 0xff;
  const base = DEFAULT_SETTINGS.find((setting) => setting.id === id);
  if (!base) throw new Error(`Unknown setting ID ${id}`);
  const scalarType = value[1] ?? 0;
  const isBoolean = base.kind === "boolean";
  if ((!isBoolean && scalarType !== 0x06) || (isBoolean && scalarType !== 0x01)) throw new Error(`Invalid scalar type for setting ${id}`);
  const view = new DataView(value.buffer, value.byteOffset, value.byteLength);
  const precision = value[2] ?? 0;
  if (precision > 6) throw new Error(`Invalid precision for setting ${id}`);
  const current = view.getFloat32(4, true);
  const wireMin = roundDecimal(view.getFloat32(8, true), precision);
  const wireMax = roundDecimal(view.getFloat32(12, true), precision);
  const wireStep = roundDecimal(view.getFloat32(16, true), precision);
  const snap = (wireValue: number): number => snapSettingNumber(wireValue, wireMin, wireStep, precision);
  return {
    ...base,
    value: isBoolean ? current !== 0 : snap(current),
    min: isBoolean ? base.min : wireMin,
    max: isBoolean ? base.max : wireMax,
    step: isBoolean ? base.step : wireStep,
    defaultValue: isBoolean ? view.getFloat32(20, true) !== 0 : snap(view.getFloat32(20, true)),
  };
}

function roundDecimal(value: number, precision: number): number {
  if (!Number.isFinite(value)) return value;
  const scale = 10 ** precision;
  return Math.round((value + Number.EPSILON) * scale) / scale;
}

function snapSettingNumber(value: number, minimum: number, step: number, precision: number): number {
  if (!Number.isFinite(value)) return value;
  const snapped = step > 0 ? minimum + Math.round((value - minimum) / step) * step : value;
  return roundDecimal(snapped, precision);
}

export interface LiveHeadRecord {
  traceEpoch: number;
  status: number;
  sampleCount: number;
  totalTicks: number;
}

export interface LiveRecord {
  heads: Record<HeadId, LiveHeadRecord>;
  calibration: number;
}

const LIVE_FRAME_PERIOD_MS = 250;

const RefreshScope = {
  State: 1 << 0,
  Config: 1 << 1,
  Catalog: 1 << 2,
  Head1Trace: 1 << 3,
  Head2Trace: 1 << 4,
  ReferenceTrace: 1 << 5,
  InferFromState: 1 << 6,
} as const;

const HEAVY_REFRESH_SCOPE = RefreshScope.Config
  | RefreshScope.Catalog
  | RefreshScope.Head1Trace
  | RefreshScope.Head2Trace
  | RefreshScope.ReferenceTrace;

const ALL_TRACE_SCOPE = RefreshScope.Head1Trace
  | RefreshScope.Head2Trace
  | RefreshScope.ReferenceTrace;

class RefreshSupersededError extends Error {}
class TargetedVersionMismatchError extends Error {}

export class UncertainCommandError extends Error {
  readonly recovery: Promise<void>;

  constructor(message: string, recovery: Promise<void>) {
    super(message);
    this.name = "UncertainCommandError";
    this.recovery = recovery;
  }
}

function acceptedOutcome(code: CommandResultCode): boolean {
  return code === "ok"
    || code === "queued"
    || code === "persistence_pending"
    || code === "confirmation_required";
}

type StateProjection = (
  state: DeviceState,
  outcome: CommandOutcome,
  tlvs: readonly Tlv[],
) => boolean;

interface MutationPlan {
  successScope: number;
  recoveryScope: number;
  projectionFallbackScope?: number;
  project?: StateProjection;
  projectWhenQueued?: boolean;
  projectWhenConfirmationRequired?: boolean;
  completionAfterPersistencePending?: boolean;
  cancelProjection?: (state: DeviceState) => void;
}

interface PendingCompletion {
  plan: MutationPlan;
}

function headTraceScope(head: HeadId): number {
  return head === 1 ? RefreshScope.Head1Trace : RefreshScope.Head2Trace;
}

export function parseLiveRecord(value: Uint8Array): LiveRecord {
  if (value.length !== 20) throw new Error("Invalid live record");
  const view = new DataView(value.buffer, value.byteOffset, value.byteLength);
  const calibration = view.getFloat32(16, true);
  if (!Number.isFinite(calibration) || calibration < 0) throw new Error("Invalid live calibration");
  return {
    heads: {
      1: { traceEpoch: value[0] ?? 0, status: value[2] ?? 0, sampleCount: view.getUint16(4, true), totalTicks: view.getUint32(8, true) },
      2: { traceEpoch: value[1] ?? 0, status: value[3] ?? 0, sampleCount: view.getUint16(6, true), totalTicks: view.getUint32(12, true) },
    },
    calibration,
  };
}

export function applyLiveHeadRecord(previous: HeadState, record: LiveHeadRecord, calibration: number): HeadState {
  const active = record.status >= 1 && record.status <= 3;
  const firstEpoch = previous.liveTraceEpoch === undefined;
  const epochChanged = !firstEpoch && previous.liveTraceEpoch !== record.traceEpoch;

  // The first packet establishes the epoch for a trace fetched through
  // GET_TRACE. A matching packet after that trace becomes retained is delayed
  // telemetry and must not resurrect the old live graph.
  if (!firstEpoch && !epochChanged && previous.wireTraceKind !== 1) return previous;
  if (epochChanged && !active) return previous;

  const statusUpdate: HeadState = {
    ...previous,
    liveTraceEpoch: record.traceEpoch,
    status: statusFromWire(record.status),
    phase: phaseFromStatus(record.status, previous.demoActive && record.status === 3),
    detectorActive: active,
    demoActive: previous.demoActive && record.status === 3,
  };

  // The firmware emits one zero-count inactive transition for the epoch that
  // just ended. Retain the completed graph and its absolute baseline, but
  // close the live generation so a delayed same-epoch packet cannot revive it.
  if (!active && previous.wireTraceKind === 1) {
    return { ...statusUpdate, liveConnectionResetPending: false, wireTraceKind: 2 };
  }

  const startsNewTrace = active && (epochChanged || (firstEpoch && previous.wireTraceKind !== 1));
  const acceptsSamples = previous.wireTraceKind === 1 || startsNewTrace;
  if (!acceptsSamples) {
    return statusUpdate;
  }

  // Trace epochs are connection-local on the controller. After reconnecting,
  // the same numeric epoch may therefore describe the continuing shot. The
  // first packet establishes fresh absolute cursors without duplicating the
  // cached points or rejecting the packet against the old connection's
  // baseline; the following packet resumes normal delta plotting.
  const reconnectBaselineUnknown = firstEpoch
    && previous.wireTraceKind === 1
    && previous.liveConnectionResetPending === true
    && previous.trace.points.length > 0;
  if (active && reconnectBaselineUnknown) {
    return {
      ...statusUpdate,
      elapsedMs: record.sampleCount * LIVE_FRAME_PERIOD_MS,
      volumeMl: record.totalTicks * calibration,
      trace: previous.trace,
      liveSampleIndex: record.sampleCount,
      liveTraceEpoch: record.traceEpoch,
      liveTotalTicks: record.totalTicks,
      liveConnectionResetPending: false,
      wireTraceKind: 1,
    };
  }

  const points = startsNewTrace ? [] : previous.trace.points;
  const baselineCount = startsNewTrace ? 0 : (previous.liveSampleIndex ?? 0);
  const baselineTicks = startsNewTrace ? 0 : (previous.liveTotalTicks ?? 0);
  if (record.sampleCount < baselineCount || record.totalTicks < baselineTicks) {
    return statusUpdate;
  }

  const gap = record.sampleCount - baselineCount;
  const tickGap = record.totalTicks - baselineTicks;
  if (tickGap > 0 && calibration <= 0) return statusUpdate;
  const nextPoints = gap === 0 ? points : [...points];
  for (let frame = 1; frame <= gap; frame += 1) {
    const absoluteCount = baselineCount + frame;
    const interpolatedTicks = baselineTicks + tickGap * frame / gap;
    nextPoints.push({
      elapsedMs: absoluteCount * LIVE_FRAME_PERIOD_MS,
      milliliters: interpolatedTicks * calibration,
    });
  }

  return {
    ...statusUpdate,
    elapsedMs: record.sampleCount * LIVE_FRAME_PERIOD_MS,
    volumeMl: record.totalTicks * calibration,
    trace: startsNewTrace ? { points: nextPoints } : { ...previous.trace, points: nextPoints },
    liveSampleIndex: record.sampleCount,
    liveTraceEpoch: record.traceEpoch,
    liveTotalTicks: record.totalTicks,
    liveConnectionResetPending: false,
    wireTraceKind: 1,
  };
}

export class BleRemoteApi implements RemoteApi {
  readonly #transport: BleTransport;
  readonly #channel: CommandChannel;
  #listeners = new Set<(event: RemoteEvent) => void>();
  #state?: DeviceState;
  #syncing?: Promise<DeviceState>;
  #refreshing?: Promise<DeviceState>;
  #mutationRecovery?: Promise<void>;
  #pendingMutationRecoveryScope = 0;
  #refreshTimer?: number;
  #pendingRefreshScope = 0;
  #deferredRefreshScope = 0;
  #pendingCompletions = new Map<number, PendingCompletion>();
  #confirmationPlans = new Map<bigint, MutationPlan>();
  #liveActivityGeneration = 0;
  #latestLiveRecord?: Uint8Array;
  #refreshRetryCount = 0;
  #refreshInvalidationGeneration = 0;
  #connectionEpoch = 0;
  #stateConnectionEpoch?: number;
  readonly #credentialStore?: AccessCredentialStore;
  #authenticated = false;
  #enrollmentControllerId?: string;
  #authChallenge?: Uint8Array;

  constructor(transport: BleTransport, credentialStore?: AccessCredentialStore) {
    this.#transport = transport;
    this.#credentialStore = transport.applicationAuthRequired
      ? (credentialStore ?? new AccessCredentialStore())
      : undefined;
    this.#channel = new CommandChannel(transport);
    this.#channel.onEvent((message) => this.#handleEvent(message));
    transport.onLive((record) => this.#applyLiveSample(record));
    transport.onDisconnect((reason) => {
      this.#authenticated = false;
      this.#channel.setSession();
      this.#beginConnectionEpoch(false);
      this.#emit({ type: "disconnected", reason });
    });
    transport.onReconnect(() => {
      this.#beginConnectionEpoch(true);
      void this.#restoreAuthenticatedConnection();
    });
  }

  get connected(): boolean {
    return this.#transport.connected &&
      (!this.#transport.applicationAuthRequired || this.#authenticated);
  }
  get canReconnect(): boolean { return this.#transport.canReconnect; }
  get deviceId(): string | undefined { return this.#transport.deviceId; }
  get enrollmentControllerId(): string | undefined { return this.#enrollmentControllerId; }

  async pair(): Promise<DeviceState> {
    try {
      await this.#transport.pair();
      this.#beginConnectionEpoch(true);
      await this.#authenticateStoredCredential();
      return await this.sync();
    } catch (error) {
      if (this.#transport.connected && !(error instanceof EnrollmentRequiredError)
          && !(error instanceof InvalidAccessKeyError)) {
        this.#transport.disconnect(
          error instanceof Error ? error.message : "Initial controller sync failed.",
          { reconnect: true },
        );
      }
      throw error;
    }
  }

  async reconnect(preferredDeviceId?: string): Promise<DeviceState> {
    try {
      await this.#transport.reconnect(preferredDeviceId);
      this.#beginConnectionEpoch(true);
      await this.#authenticateStoredCredential();
      return await this.sync();
    } catch (error) {
      if (this.#transport.connected && !(error instanceof EnrollmentRequiredError)
          && !(error instanceof InvalidAccessKeyError)) {
        this.#transport.disconnect(
          error instanceof Error ? error.message : "Controller resync failed.",
          { reconnect: true },
        );
      }
      throw error;
    }
  }

  async enroll(accessCode: string): Promise<DeviceState> {
    if (!this.#transport.connected || !this.#transport.applicationAuthRequired) {
      throw new Error("Pair with the controller before entering its access key.");
    }
    const controllerId = this.#enrollmentControllerId;
    const challenge = this.#authChallenge;
    if (!controllerId || !challenge) {
      await this.#readAuthenticationChallenge();
    }
    const key = decodeAccessCode(accessCode);
    await this.#authenticateKey(
      this.#enrollmentControllerId!,
      this.#authChallenge!,
      key,
    );
    this.#credentialStore!.set(this.#enrollmentControllerId!, key);
    return this.sync();
  }

  disconnect(): void { this.#transport.disconnect(); }

  async #restoreAuthenticatedConnection(): Promise<void> {
    try {
      await this.#authenticateStoredCredential();
      // A physical GATT reconnect is not healthy until application auth and the
      // essential State/Config consistency check both succeed. Only then reset
      // the transport's reconnect backoff.
      await this.sync();
      this.#transport.markConnectionReady?.();
    } catch (error) {
      const reason = error instanceof Error ? error.message : "Controller authentication failed.";
      const requiresUserAction = error instanceof EnrollmentRequiredError || error instanceof InvalidAccessKeyError;
      this.#transport.disconnect(reason, requiresUserAction ? undefined : { reconnect: true });
    }
  }

  async #readAuthenticationChallenge(): Promise<void> {
    const response = await this.#channel.request(Opcode.GetAuthChallenge);
    if (response.status !== StatusCode.Ok) {
      throw new Error("Controller could not start application authentication.");
    }
    const controllerId = new TextDecoder("utf-8", { fatal: true }).decode(
      required(response.tlvs, TlvTag.ControllerId, 16),
    ).toUpperCase();
    if (!/^[0-9A-F]{16}$/.test(controllerId)) {
      throw new Error("Controller returned an invalid identity.");
    }
    this.#enrollmentControllerId = controllerId;
    this.#authChallenge = required(response.tlvs, TlvTag.AuthChallenge, 16).slice();
  }

  async #authenticateStoredCredential(): Promise<void> {
    this.#authenticated = false;
    this.#channel.setSession();
    if (!this.#transport.applicationAuthRequired) {
      this.#authenticated = true;
      return;
    }
    await this.#readAuthenticationChallenge();
    const key = this.#credentialStore?.get(this.#enrollmentControllerId!);
    if (!key) throw new EnrollmentRequiredError(this.#enrollmentControllerId!);
    await this.#authenticateKey(this.#enrollmentControllerId!, this.#authChallenge!, key);
  }

  async #authenticateKey(controllerId: string, challenge: Uint8Array, key: Uint8Array): Promise<void> {
    const { session, clientProof } = await ApplicationSession.create(key, controllerId, challenge);
    const response = await this.#channel.request(Opcode.Authenticate, [
      tlv.raw(TlvTag.AuthClientNonce, session.clientNonce),
      tlv.raw(TlvTag.AuthProof, clientProof),
    ]);
    if (response.status === StatusCode.Unauthorized) throw new InvalidAccessKeyError();
    if (response.status !== StatusCode.Ok) throw new Error("Controller authentication failed.");
    const serverProof = required(response.tlvs, TlvTag.AuthProof, 16);
    if (!await session.verifyServerProof(serverProof)) {
      throw new Error("Controller identity proof did not match.");
    }
    this.#channel.setSession(session);
    this.#authenticated = true;
  }

  sync(): Promise<DeviceState> {
    if (!this.#syncing) {
      const epoch = this.#connectionEpoch;
      const work = this.#performSync(epoch);
      const tracked = work
        .finally(() => {
          if (this.#syncing !== tracked || this.#connectionEpoch !== epoch) return;
          this.#syncing = undefined;
          if (this.#pendingRefreshScope) this.#armRefreshTimer();
          else this.#flushDeferredRefreshIfIdle();
        });
      this.#syncing = tracked;
    }
    return this.#syncing;
  }

  async #performSync(epoch: number, attempt = 0): Promise<DeviceState> {
    this.#assertConnectionEpoch(epoch);
    const stateLiveGeneration = this.#liveActivityGeneration;
    const stateResponse = await this.#channel.request(Opcode.GetState);
    this.#assertConnectionEpoch(epoch);
    this.#expectOk(stateResponse, "state");
    const configResponse = await this.#channel.request(Opcode.GetConfig);
    this.#assertConnectionEpoch(epoch);
    this.#expectOk(configResponse, "configuration");
    const liveChangedDuringSync = this.#liveActivityGeneration !== stateLiveGeneration;

    const sessionId = readU32(required(stateResponse.tlvs, TlvTag.Session, 4));
    const revision = readU32(required(stateResponse.tlvs, TlvTag.Revision, 4));
    const configSession = readU32(required(configResponse.tlvs, TlvTag.Session, 4));
    const configRevision = readU32(required(configResponse.tlvs, TlvTag.Revision, 4));
    if (configSession !== sessionId || configRevision !== revision) {
      if (attempt < 1) return this.#performSync(epoch, attempt + 1);
      throw new Error("Controller state changed during synchronization. Try again.");
    }
    const generation = readU32(required(stateResponse.tlvs, TlvTag.Generation, 4));
    const capabilities = readU32(required(stateResponse.tlvs, TlvTag.Capabilities, 4));
    const stateFlags = readU32(required(stateResponse.tlvs, TlvTag.StateFlags, 4));
    const selectedIdRaw = readU32(required(stateResponse.tlvs, TlvTag.SelectedSnapshotId, 4));
    const selectedSnapshotId = selectedIdRaw || undefined;
    const headRecords = stateResponse.tlvs.filter((item) => item.tag === TlvTag.HeadState);
    if (headRecords.length !== 2) throw new Error("Controller did not return both head states.");
    const previous = this.#state?.sessionId === sessionId ? this.#state : undefined;
    const parsedHeads = headRecords.map((item) => parseHead(item.value, previous?.heads[((item.value[0] ?? 0) + 1) as HeadId], Boolean(stateFlags & 0x10)));
    const head1 = parsedHeads.find((head) => head.id === 1);
    const head2 = parsedHeads.find((head) => head.id === 2);
    if (!head1 || !head2) throw new Error("Controller returned duplicate head states.");
    if (previous && selectedSnapshotId !== previous.selectedSnapshotId) {
      head1.reference = undefined;
      head2.reference = undefined;
    }

    const settingRecords = configResponse.tlvs.filter((item) => item.tag === TlvTag.SettingRecord).map((item) => parseSetting(item.value));
    settingRecords.sort((a, b) => a.id - b.id);
    if (settingRecords.length !== SETTING_COUNT || settingRecords.some((setting, index) => setting.id !== index)) {
      throw new Error("Controller returned an incomplete configuration.");
    }
    const nextState: DeviceState = {
      sessionId,
      revision,
      generation,
      deviceName: new TextDecoder("utf-8", { fatal: true }).decode(required(stateResponse.tlvs, TlvTag.DeviceName)) || "Espress9th",
      firmwareVersion: `E9 Remote v${PROTOCOL_VERSION}`,
      protocolVersion: PROTOCOL_VERSION,
      storageAvailable: Boolean(stateFlags & 0x01),
      storageNeedsRepair: Boolean(stateFlags & (1 << 1)),
      statePersistencePending: Boolean(stateFlags & (1 << 2)),
      statePersistenceFailed: readPersistenceFailed(stateResponse.tlvs),
      settingsApplyPending: Boolean(stateFlags & (1 << 3)),
      lifetimeShots: safeCounter(readU64(required(stateResponse.tlvs, TlvTag.Lifetime, 8))),
      batchShots: safeCounter(readU64(required(stateResponse.tlvs, TlvTag.Batch, 8))),
      selectedSnapshotId,
      heads: { 1: head1, 2: head2 },
      snapshots: previous?.snapshots.map((snapshot) => ({
        ...snapshot,
        active: snapshot.id === selectedSnapshotId,
      })) ?? [],
      settings: settingRecords,
      capabilities: {
        demo: Boolean(capabilities & (1 << 2)) && Boolean(stateFlags & (1 << 5)),
        repairStorage: Boolean(capabilities & (1 << 4)),
        configPatch: Boolean(capabilities & (1 << 8)),
        maxSnapshots: 12,
      },
    };
    this.#assertConnectionEpoch(epoch);
    if (this.#state && this.#state.sessionId !== sessionId) {
      this.#pendingCompletions.clear();
      this.#confirmationPlans.clear();
      this.#deferredRefreshScope = 0;
    }
    this.#state = nextState;
    this.#stateConnectionEpoch = epoch;

    if (liveChangedDuringSync && this.#latestLiveRecord) {
      const latest = parseLiveRecord(this.#latestLiveRecord);
      for (const head of [1, 2] as const) {
        const base = previous
          ? this.#liveReplayBase(this.#state.heads[head], previous.heads[head])
          : this.#state.heads[head];
        this.#state.heads[head] = applyLiveHeadRecord(base, latest.heads[head], latest.calibration);
      }
      this.#pendingRefreshScope |= RefreshScope.State | RefreshScope.InferFromState;
    }
    if (previous && selectedSnapshotId !== previous.selectedSnapshotId) {
      this.#state.heads[1].reference = undefined;
      this.#state.heads[2].reference = undefined;
    }

    const traceCapable = Boolean(capabilities & (1 << 6));
    this.#assertConnectionEpoch(epoch);
    this.#emit({ type: "state", device: structuredClone(this.#state) });
    // Pairing is ready once State and Config agree. Catalog and trace transfer
    // are progressive background work so a full trace history cannot hold the
    // connection UI in its pairing state for several more seconds.
    this.#scheduleRefresh(RefreshScope.Catalog | (traceCapable ? ALL_TRACE_SCOPE : 0));
    return structuredClone(this.#state);
  }

  async #performScopedRefresh(
    scope: number,
    epoch: number,
    attempt = 0,
    invalidationGeneration = this.#refreshInvalidationGeneration,
    deferHeavy = true,
  ): Promise<DeviceState> {
    const requestedScope = scope;
    this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
    if (!this.#state || this.#stateConnectionEpoch !== epoch) return this.#performSync(epoch);

    let sessionId = this.#state.sessionId;
    let revision = this.#state.revision;
    if (scope & RefreshScope.State) {
      const stateLiveGeneration = this.#liveActivityGeneration;
      const response = await this.#channel.request(Opcode.GetState);
      this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
      this.#expectOk(response, "state");
      const liveChangedDuringState = this.#liveActivityGeneration !== stateLiveGeneration;
      sessionId = readU32(required(response.tlvs, TlvTag.Session, 4));
      if (this.#state.sessionId !== sessionId) return this.#performSync(epoch);

      const previous = this.#state;
      revision = readU32(required(response.tlvs, TlvTag.Revision, 4));
      const generation = readU32(required(response.tlvs, TlvTag.Generation, 4));
      const capabilities = readU32(required(response.tlvs, TlvTag.Capabilities, 4));
      const stateFlags = readU32(required(response.tlvs, TlvTag.StateFlags, 4));
      const selectedIdRaw = readU32(required(response.tlvs, TlvTag.SelectedSnapshotId, 4));
      const selectedSnapshotId = selectedIdRaw || undefined;
      const headRecords = response.tlvs.filter((item) => item.tag === TlvTag.HeadState);
      if (headRecords.length !== 2) throw new Error("Controller did not return both head states.");
      const parsedHeads = headRecords.map((item) => parseHead(
        item.value,
        previous.heads[((item.value[0] ?? 0) + 1) as HeadId],
        Boolean(stateFlags & 0x10),
      ));
      const head1 = parsedHeads.find((head) => head.id === 1);
      const head2 = parsedHeads.find((head) => head.id === 2);
      if (!head1 || !head2) throw new Error("Controller returned duplicate head states.");
      if (selectedSnapshotId !== previous.selectedSnapshotId) {
        head1.reference = undefined;
        head2.reference = undefined;
      }

      this.#state = {
        ...previous,
        revision,
        generation,
        deviceName: new TextDecoder("utf-8", { fatal: true }).decode(required(response.tlvs, TlvTag.DeviceName)) || "Espress9th",
        storageAvailable: Boolean(stateFlags & 0x01),
        storageNeedsRepair: Boolean(stateFlags & (1 << 1)),
        statePersistencePending: Boolean(stateFlags & (1 << 2)),
        statePersistenceFailed: readPersistenceFailed(response.tlvs),
        settingsApplyPending: Boolean(stateFlags & (1 << 3)),
        lifetimeShots: safeCounter(readU64(required(response.tlvs, TlvTag.Lifetime, 8))),
        batchShots: safeCounter(readU64(required(response.tlvs, TlvTag.Batch, 8))),
        selectedSnapshotId,
        heads: { 1: head1, 2: head2 },
        snapshots: previous.snapshots.map((snapshot) => ({ ...snapshot, active: snapshot.id === selectedSnapshotId })),
        capabilities: {
          demo: Boolean(capabilities & (1 << 2)) && Boolean(stateFlags & (1 << 5)),
          repairStorage: Boolean(capabilities & (1 << 4)),
          configPatch: Boolean(capabilities & (1 << 8)),
          maxSnapshots: previous.capabilities.maxSnapshots,
        },
      };
      this.#stateConnectionEpoch = epoch;
      if (liveChangedDuringState && this.#latestLiveRecord) {
        const latest = parseLiveRecord(this.#latestLiveRecord);
        for (const head of [1, 2] as const) {
          const base = this.#liveReplayBase(this.#state.heads[head], previous.heads[head]);
          this.#state.heads[head] = applyLiveHeadRecord(base, latest.heads[head], latest.calibration);
        }
      }
      if (selectedSnapshotId !== previous.selectedSnapshotId) this.#clearReference(this.#state);

      if (scope & RefreshScope.InferFromState) {
        if (revision !== previous.revision) scope |= RefreshScope.Config | RefreshScope.Catalog;
        if (selectedSnapshotId !== previous.selectedSnapshotId) scope |= RefreshScope.Catalog | RefreshScope.ReferenceTrace;
        for (const head of [1, 2] as const) {
          const before = previous.heads[head];
          const after = this.#state.heads[head];
          if (before.wireTraceKind !== after.wireTraceKind
            || (!after.detectorActive && (before.elapsedMs !== after.elapsedMs || before.volumeMl !== after.volumeMl))) {
            scope |= headTraceScope(head);
          }
        }
      }
    }

    const heavyScope = scope & HEAVY_REFRESH_SCOPE;
    if (deferHeavy && this.#headsActive() && heavyScope) {
      this.#deferredRefreshScope |= heavyScope;
      scope &= ~HEAVY_REFRESH_SCOPE;
    }

    let configResponse: ResponseEnvelope | undefined;
    let catalogResponse: ResponseEnvelope | undefined;
    if (scope & RefreshScope.Config) {
      configResponse = await this.#channel.request(Opcode.GetConfig);
      this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
      this.#expectOk(configResponse, "configuration");
    }
    if (scope & RefreshScope.Catalog) {
      catalogResponse = await this.#channel.request(Opcode.ListSnapshots);
      this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
      this.#expectOk(catalogResponse, "snapshot catalog");
    }
    for (const candidate of [configResponse, catalogResponse]) {
      if (!candidate) continue;
      const candidateSession = readU32(required(candidate.tlvs, TlvTag.Session, 4));
      const candidateRevision = readU32(required(candidate.tlvs, TlvTag.Revision, 4));
      if (candidateSession !== sessionId || candidateRevision !== revision) {
        if (attempt < 1) return this.#performScopedRefresh(
          requestedScope | RefreshScope.State,
          epoch,
          attempt + 1,
          invalidationGeneration,
          deferHeavy,
        );
        throw new Error("Controller state changed during targeted refresh. Try again.");
      }
    }
    this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
    if (configResponse) this.#applyConfigResponse(configResponse);
    if (catalogResponse) this.#applyCatalogResponse(catalogResponse);
    if (configResponse || catalogResponse) {
      this.#emit({ type: "state", device: structuredClone(this.#state) });
    }

    const traceGuard = this.#liveActivityGeneration;
    const traceSources: Array<[number, number]> = [
      [2, RefreshScope.ReferenceTrace],
      [0, RefreshScope.Head1Trace],
      [1, RefreshScope.Head2Trace],
    ];
    for (const [source, traceScope] of traceSources) {
      if (!(scope & traceScope)) continue;
      if (source === 2 && !this.#state?.selectedSnapshotId) {
        this.#state.heads[1].reference = undefined;
        this.#state.heads[2].reference = undefined;
        continue;
      }
      if ((deferHeavy && this.#headsActive()) || (source !== 2 && this.#liveActivityGeneration !== traceGuard)) {
        this.#deferredRefreshScope |= traceScope;
        continue;
      }
      const liveGuard = source === 2 ? undefined : traceGuard;
      let loaded = false;
      try {
        loaded = await this.#loadTrace(source, liveGuard, epoch, invalidationGeneration);
      } catch (error) {
        if (error instanceof TargetedVersionMismatchError && attempt < 1) {
          return this.#performScopedRefresh(
            requestedScope | RefreshScope.State,
            epoch,
            attempt + 1,
            invalidationGeneration,
            deferHeavy,
          );
        }
        throw error;
      }
      if (!loaded) {
        this.#deferredRefreshScope |= traceScope;
      } else if (source === 2) {
        // Publish the selected comparison without holding it behind the two
        // retained head histories, which can each take several seconds.
        this.#emit({ type: "state", device: structuredClone(this.#state) });
      }
    }
    this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
    this.#emit({ type: "state", device: structuredClone(this.#state) });
    return structuredClone(this.#state);
  }

  #applyConfigResponse(response: ResponseEnvelope): void {
    if (!this.#state) return;
    const settings = response.tlvs.filter((item) => item.tag === TlvTag.SettingRecord).map((item) => parseSetting(item.value));
    settings.sort((a, b) => a.id - b.id);
    if (settings.length !== SETTING_COUNT || settings.some((setting, index) => setting.id !== index)) {
      throw new Error("Controller returned an incomplete configuration.");
    }
    this.#state.settings = settings;
  }

  #applyCatalogResponse(response: ResponseEnvelope): void {
    if (!this.#state) return;
    const snapshots = response.tlvs
      .filter((item) => item.tag === TlvTag.SnapshotSummary)
      .map((item) => parseSnapshot(item.value, this.#state?.selectedSnapshotId));
    if (snapshots.length > 12) throw new Error("Controller returned too many snapshots.");
    this.#state.snapshots = snapshots;
  }

  async #loadTrace(
    source: number,
    expectedLiveGeneration: number | undefined,
    epoch: number,
    invalidationGeneration: number,
  ): Promise<boolean> {
    const response = await this.#channel.request(Opcode.GetTrace, [tlv.u8(TlvTag.TraceSource, source)]);
    this.#assertScopedRefreshCurrent(epoch, invalidationGeneration);
    if (expectedLiveGeneration !== undefined && this.#liveActivityGeneration !== expectedLiveGeneration) return false;
    const traceSession = readU32(required(response.tlvs, TlvTag.Session, 4));
    const traceRevision = readU32(required(response.tlvs, TlvTag.Revision, 4));
    if (!this.#state || traceSession !== this.#state.sessionId || traceRevision !== this.#state.revision) {
      throw new TargetedVersionMismatchError("Controller trace changed during synchronization.");
    }
    if (response.status === StatusCode.NotFound) {
      if (!this.#state) return true;
      if (source === 2) {
        this.#state.heads[1].reference = undefined;
        this.#state.heads[2].reference = undefined;
      } else {
        const head = (source + 1) as HeadId;
        this.#state.heads[head] = {
          ...this.#state.heads[head],
          trace: { points: [] },
          liveSampleIndex: 0,
          liveTotalTicks: 0,
        };
      }
      return true;
    }
    this.#expectOk(response, "trace");
    const metadata = exact(response.tlvs, TlvTag.TraceMeta, 88);
    if (!metadata || !this.#state) return true;
    const parsed = parseTrace(metadata, exact(response.tlvs, TlvTag.Ticks));
    if (parsed.source !== source) throw new Error("Controller returned a trace for the wrong source.");
    if (parsed.source === 2) {
      this.#state.heads[1].reference = parsed.trace;
      this.#state.heads[2].reference = parsed.trace;
    } else {
      const head = (parsed.source + 1) as HeadId;
      const sampleCount = new DataView(metadata.buffer, metadata.byteOffset, metadata.byteLength).getUint16(22, true);
      const totalTicks = new DataView(metadata.buffer, metadata.byteOffset, metadata.byteLength).getUint32(12, true);
      this.#state.heads[head] = {
        ...this.#state.heads[head],
        trace: parsed.trace,
        volumeMl: parsed.volumeMl,
        elapsedMs: parsed.elapsedMs,
        score: parsed.score,
        liveSampleIndex: sampleCount,
        liveTotalTicks: totalTicks,
      };
    }
    return true;
  }

  #expectOk(response: ResponseEnvelope, label: string): void {
    if (response.status !== StatusCode.Ok) throw new Error(`Controller could not return ${label} (${STATUS_CODES[response.status] ?? "unknown"}).`);
  }

  #preconditions(): Tlv[] {
    if (!this.#state) throw new Error("Reconnect before changing controller state.");
    return [tlv.u32(TlvTag.ExpectedSession, this.#state.sessionId), tlv.u32(TlvTag.ExpectedRevision, this.#state.revision)];
  }

  async #mutate(
    opcode: Opcode,
    values: readonly Tlv[] = [],
    suppliedPlan?: MutationPlan,
  ): Promise<CommandOutcome> {
    if (this.#mutationRecovery) await this.#mutationRecovery;
    const plan = suppliedPlan ?? this.#mutationPlan(opcode, values);
    this.#refreshInvalidationGeneration += 1;
    const initialSession = this.#state?.sessionId;
    const initialSelection = this.#state?.selectedSnapshotId;
    const loadTarget = opcode === Opcode.Load
      ? readU32(required(values, TlvTag.SnapshotId, 4))
      : undefined;
    const requestValues = [...this.#preconditions(), ...values];
    let response: ResponseEnvelope;
    try {
      response = await this.#channel.request(opcode, requestValues);
    } catch (error) {
      // A missing application response is uncertain: the controller may have
      // executed the command after the last acknowledged GATT fragment. Never
      // project or blindly retry it; reconcile the affected resources instead.
      this.#refreshInvalidationGeneration += 1;
      const recovery = this.#transport.connected
        ? this.#startMutationRecovery(plan.recoveryScope)
        : Promise.resolve();
      const message = error instanceof Error ? error.message : "The controller response was incomplete.";
      throw new UncertainCommandError(message, recovery);
    }
    this.#refreshInvalidationGeneration += 1;
    let outcome: CommandOutcome;
    let responseStale = false;
    try {
      const code = STATUS_CODES[response.status];
      responseStale = this.#isOlderVersion(this.#outcomeVersion(response.tlvs));
      outcome = this.#decodeOutcome(
        response.status,
        response.tlvs,
        response.requestId,
        false,
        !responseStale && code !== undefined && acceptedOutcome(code),
      );
    } catch (error) {
      const recovery = this.#transport.connected
        ? this.#startMutationRecovery(plan.recoveryScope)
        : Promise.resolve();
      const message = error instanceof Error ? error.message : "The controller response was malformed.";
      throw new UncertainCommandError(message, recovery);
    }

    if (outcome.code === "conflict" && loadTarget !== undefined && initialSession !== undefined) {
      try {
        const fresh = await this.#performScopedRefresh(RefreshScope.State, this.#connectionEpoch);
        if (fresh.sessionId === initialSession) {
          if (fresh.selectedSnapshotId === loadTarget) {
            // The requested postcondition already exists. Sending LOAD again
            // would needlessly persist NVS and increment controller revision.
            outcome = { ...outcome, code: "ok", revision: fresh.revision, message: "Snapshot is already loaded." };
          } else if (fresh.selectedSnapshotId === initialSelection) {
            // Only an unrelated controller field changed. Rebase once, while
            // preserving the reference-selection lost-update guard.
            this.#refreshInvalidationGeneration += 1;
            response = await this.#channel.request(opcode, [...this.#preconditions(), ...values]);
            this.#refreshInvalidationGeneration += 1;
            const code = STATUS_CODES[response.status];
            responseStale = this.#isOlderVersion(this.#outcomeVersion(response.tlvs));
            outcome = this.#decodeOutcome(
              response.status,
              response.tlvs,
              response.requestId,
              false,
              !responseStale && code !== undefined && acceptedOutcome(code),
            );
          } else {
            outcome = {
              ...outcome,
              revision: fresh.revision,
              message: "Another controller changed the loaded reference. The latest state is now shown.",
            };
          }
        }
      } catch (error) {
        if (!this.#transport.connected) throw error;
        // Keep the original conflict. The scheduled recovery below continues
        // reconciling a transient failed compact State read.
      }
    }

    this.#pendingCompletions.delete(response.requestId);
    if (responseStale && outcome.confirmation) {
      outcome = {
        ...outcome,
        confirmation: undefined,
        message: "The command was accepted before newer controller state; the latest state is being verified.",
      };
    }
    const shouldProject = !responseStale && (outcome.code === "ok"
      || outcome.code === "persistence_pending"
      || (outcome.code === "queued" && plan.projectWhenQueued)
      || (outcome.code === "confirmation_required" && plan.projectWhenConfirmationRequired));
    const projectionComplete = !shouldProject || this.#applyProjection(plan, outcome, response.tlvs);

    if (!responseStale && outcome.confirmation) {
      this.#confirmationPlans.set(outcome.confirmation.token, plan);
    } else if (outcome.code === "queued"
        || (outcome.code === "persistence_pending" && plan.completionAfterPersistencePending)) {
      this.#pendingCompletions.set(response.requestId, { plan });
    }

    if (responseStale) {
      void this.#startMutationRecovery(plan.recoveryScope).catch(() => undefined);
    } else if (["ok", "queued", "persistence_pending"].includes(outcome.code)) {
      const successScope = projectionComplete
        ? plan.successScope
        : (plan.projectionFallbackScope ?? plan.recoveryScope);
      this.#scheduleRefresh(successScope | (outcome.code === "persistence_pending" ? RefreshScope.State : 0));
    } else if (outcome.code === "conflict") {
      await this.#startMutationRecovery(plan.recoveryScope);
    } else if (outcome.code === "not_found") {
      this.#scheduleRefresh(plan.recoveryScope);
    }
    return outcome;
  }

  #mutationPlan(opcode: Opcode, values: readonly Tlv[]): MutationPlan {
    const recoveryScope = this.#mutationRecoveryScope(opcode, values);
    switch (opcode) {
      case Opcode.Save: {
        const wireHead = readU8(required(values, TlvTag.Head, 1));
        const head = (wireHead + 1) as HeadId;
        return {
          successScope: 0,
          recoveryScope,
          projectionFallbackScope: RefreshScope.Catalog,
          projectWhenQueued: true,
          projectWhenConfirmationRequired: true,
          cancelProjection: (state) => { state.heads[head].pendingSave = false; },
          project: (state, outcome, tlvs) => {
            state.heads[head].pendingSave = outcome.code === "queued"
              || outcome.code === "confirmation_required";
            if (outcome.code === "queued" || outcome.code === "confirmation_required") return true;
            state.heads[head].pendingSave = false;
            return this.#projectSavedSnapshot(state, tlvs);
          },
        };
      }
      case Opcode.Load: {
        const id = readU32(required(values, TlvTag.SnapshotId, 4));
        return {
          successScope: RefreshScope.ReferenceTrace,
          recoveryScope,
          projectionFallbackScope: RefreshScope.Catalog | RefreshScope.ReferenceTrace,
          project: (state) => {
            const found = state.snapshots.some((snapshot) => snapshot.id === id);
            if (state.selectedSnapshotId !== id) this.#clearReference(state);
            state.selectedSnapshotId = id;
            state.snapshots = state.snapshots.map((snapshot) => ({ ...snapshot, active: snapshot.id === id }));
            return found;
          },
        };
      }
      case Opcode.Unload:
        return {
          successScope: 0,
          recoveryScope,
          project: (state) => {
            state.selectedSnapshotId = undefined;
            state.snapshots = state.snapshots.map((snapshot) => ({ ...snapshot, active: false }));
            this.#clearReference(state);
            return true;
          },
        };
      case Opcode.Rename: {
        const id = readU32(required(values, TlvTag.SnapshotId, 4));
        const name = new TextDecoder("utf-8", { fatal: true }).decode(required(values, TlvTag.SnapshotName));
        return {
          successScope: 0,
          recoveryScope,
          projectionFallbackScope: RefreshScope.Catalog,
          project: (state) => {
            const found = state.snapshots.some((snapshot) => snapshot.id === id);
            state.snapshots = state.snapshots.map((snapshot) => snapshot.id === id ? { ...snapshot, name } : snapshot);
            return found;
          },
        };
      }
      case Opcode.Delete: {
        const id = readU32(required(values, TlvTag.SnapshotId, 4));
        return {
          successScope: 0,
          recoveryScope,
          project: (state) => {
            state.snapshots = state.snapshots.filter((snapshot) => snapshot.id !== id);
            if (state.selectedSnapshotId === id) {
              state.selectedSnapshotId = undefined;
              this.#clearReference(state);
            }
            return true;
          },
        };
      }
      case Opcode.ClearGraph: {
        const wireHead = readU8(required(values, TlvTag.Head, 1));
        const head = (wireHead + 1) as HeadId;
        return {
          successScope: 0,
          recoveryScope,
          project: (state) => {
            const previous = state.heads[head];
            state.heads[head] = {
              ...previous,
              phase: "idle",
              status: "last",
              reasonFlags: 0,
              detectorActive: false,
              demoActive: false,
              saveAvailable: false,
              volumeMl: 0,
              elapsedMs: 0,
              score: undefined,
              trace: { points: [] },
              pendingSave: false,
              liveSampleIndex: 0,
              liveTraceEpoch: undefined,
              liveTotalTicks: 0,
              liveConnectionResetPending: false,
              wireTraceKind: 0,
            };
            return true;
          },
        };
      }
      case Opcode.RunDemo: {
        const wireHead = readU8(required(values, TlvTag.Head, 1));
        const head = (wireHead + 1) as HeadId;
        return {
          successScope: 0,
          recoveryScope,
          project: (state) => {
            this.#deferredRefreshScope |= headTraceScope(head);
            const previous = state.heads[head];
            state.heads[head] = {
              ...previous,
              phase: "demo",
              status: "live",
              detectorActive: false,
              demoActive: true,
              saveAvailable: false,
              volumeMl: 0,
              elapsedMs: 0,
              score: undefined,
              trace: { points: [] },
              pendingSave: false,
              liveSampleIndex: 0,
              liveTraceEpoch: undefined,
              liveTotalTicks: 0,
              liveConnectionResetPending: false,
              wireTraceKind: 1,
            };
            return true;
          },
        };
      }
      case Opcode.ResetBatch:
        return {
          successScope: 0,
          recoveryScope,
          project: (state) => { state.batchShots = 0; return true; },
        };
      case Opcode.RepairStorage:
        return {
          successScope: 0,
          recoveryScope,
          project: (state, outcome) => {
            state.storageAvailable = outcome.code === "ok";
            state.storageNeedsRepair = false;
            state.snapshots = [];
            state.selectedSnapshotId = undefined;
            this.#clearReference(state);
            return true;
          },
        };
      default:
        return { successScope: 0, recoveryScope };
    }
  }

  #mutationRecoveryScope(opcode: Opcode, values: readonly Tlv[]): number {
    const state = RefreshScope.State;
    switch (opcode) {
      case Opcode.SetConfig:
      case Opcode.PatchConfig:
        return state | RefreshScope.Config;
      case Opcode.Save:
        return state | RefreshScope.Catalog;
      case Opcode.Load:
        return state | RefreshScope.Catalog | RefreshScope.ReferenceTrace;
      case Opcode.Unload:
        return state | RefreshScope.ReferenceTrace;
      case Opcode.Rename:
        return state | RefreshScope.Catalog;
      case Opcode.Delete:
      case Opcode.RepairStorage:
        return state | RefreshScope.Catalog | RefreshScope.ReferenceTrace;
      case Opcode.ClearGraph:
      case Opcode.RunDemo: {
        const head = values.find((item) => item.tag === TlvTag.Head)?.value[0];
        return state | headTraceScope(head === 1 ? 2 : 1);
      }
      case Opcode.ResetBatch:
      case Opcode.CancelPending:
        return state;
      default:
        return state;
    }
  }

  #applyProjection(plan: MutationPlan, outcome: CommandOutcome, tlvs: readonly Tlv[]): boolean {
    if (!this.#state) return plan.project === undefined;
    try {
      const persistenceChanged = outcome.code === "persistence_pending"
        && (!this.#state.statePersistencePending || this.#state.statePersistenceFailed);
      if (outcome.code === "persistence_pending") {
        this.#state.statePersistencePending = true;
        this.#state.statePersistenceFailed = false;
      }
      const complete = plan.project?.(this.#state, outcome, tlvs) ?? true;
      if (plan.project || persistenceChanged) {
        this.#emit({ type: "state", device: structuredClone(this.#state) });
      }
      return complete;
    } catch (error) {
      console.warn("Controller ACK could not be projected locally; falling back to synchronization", error);
      return false;
    }
  }

  #projectSavedSnapshot(state: DeviceState, tlvs: readonly Tlv[]): boolean {
    const record = exact(tlvs, TlvTag.SnapshotSummary);
    if (!record) return false;
    const summary = parseSnapshot(record, state.selectedSnapshotId);
    const removed = readU32(exact(tlvs, TlvTag.RemovedSnapshotId, 4));
    const existingIndex = state.snapshots.findIndex((snapshot) => snapshot.id === summary.id);
    const retained = state.snapshots.filter((snapshot) => snapshot.id !== summary.id && (!removed || snapshot.id !== removed));
    if (existingIndex < 0 && retained.length >= state.capabilities.maxSnapshots) return false;
    if (removed && state.selectedSnapshotId === removed) {
      state.selectedSnapshotId = undefined;
      this.#clearReference(state);
    }
    if (existingIndex >= 0 && !removed) {
      state.snapshots = state.snapshots.map((snapshot) => snapshot.id === summary.id ? summary : snapshot);
    } else {
      state.snapshots = [summary, ...retained];
    }
    return true;
  }

  #clearReference(state: DeviceState): void {
    state.heads[1].reference = undefined;
    state.heads[2].reference = undefined;
  }

  #outcomeVersion(tlvs: readonly Tlv[]): { session: number; revision: number; generation: number } {
    return {
      session: readU32(required(tlvs, TlvTag.Session, 4)),
      revision: readU32(required(tlvs, TlvTag.Revision, 4)),
      generation: readU32(required(tlvs, TlvTag.Generation, 4)),
    };
  }

  #isOlderVersion(version: { session: number; revision: number; generation: number }): boolean {
    return Boolean(this.#state
      && version.session === this.#state.sessionId
      && (version.revision < this.#state.revision || version.generation < this.#state.generation));
  }

  #decodeOutcome(
    status: StatusCode,
    tlvs: readonly Tlv[],
    requestId: number,
    deferred = false,
    updateVersion?: boolean,
  ): CommandOutcome {
    const code = STATUS_CODES[status];
    if (!code) throw new Error("Controller returned an unknown status.");
    const { session, revision, generation } = this.#outcomeVersion(tlvs);
    if (this.#state && session !== this.#state.sessionId) {
      throw new Error("Controller restarted while the operation was running. Reconnect to resync.");
    }
    if (this.#state && (updateVersion ?? acceptedOutcome(code))) {
      this.#state.revision = revision;
      this.#state.generation = generation;
    }
    if (code === "confirmation_required") {
      const token = readU64(required(tlvs, TlvTag.Token, 8));
      const pending = readU8(required(tlvs, TlvTag.PendingOp, 1));
      const expiry = readU32(required(tlvs, TlvTag.ConfirmationExpiryMs, 4));
      const copy = {
        1: ["Delete this snapshot?", "The stored reference will be removed from the controller."],
        2: ["Reset batch counter?", "Lifetime shots are not affected."],
        3: ["Repair snapshot storage?", "All snapshots will be erased. Detector settings and counters are preserved."],
        4: ["Replace the oldest snapshot?", "Snapshot storage is full. The oldest reference will be replaced."],
      }[pending] ?? ["Confirm this operation?", "The controller requires explicit confirmation."];
      return { code, requestId, revision, confirmation: { token, expiresAt: Date.now() + expiry, title: copy[0] ?? "Confirm operation", detail: copy[1] ?? "", destructive: true } };
    }
    const message = deferred ? {
      ok: "Queued operation completed.",
      busy: "Queued operation could not run because the controller is busy.",
      invalid: "Queued operation was rejected by the controller.",
      not_found: "Queued operation could not find its target.",
      conflict: "Queued operation became stale. The latest controller state was loaded.",
      storage_unavailable: "Queued operation failed because snapshot storage is unavailable.",
      unauthorized: "Controller authorization expired. Reconnect and authenticate again.",
      persistence_pending: "The change is applied; its storage write is still finishing.",
      queued: "The controller kept the operation queued.",
    }[code] : undefined;
    return { code, requestId, revision, message };
  }

  saveSnapshot(head: HeadId, name: string, _replaceId?: number): Promise<CommandOutcome> {
    return this.#mutate(Opcode.Save, [tlv.u8(TlvTag.Head, head - 1), tlv.text(TlvTag.SnapshotName, checkedName(name))]);
  }
  loadSnapshot(id: number): Promise<CommandOutcome> { return this.#mutate(Opcode.Load, [tlv.u32(TlvTag.SnapshotId, id)]); }
  unloadSnapshot(): Promise<CommandOutcome> { return this.#mutate(Opcode.Unload); }
  renameSnapshot(id: number, name: string): Promise<CommandOutcome> { return this.#mutate(Opcode.Rename, [tlv.u32(TlvTag.SnapshotId, id), tlv.text(TlvTag.SnapshotName, checkedName(name))]); }
  deleteSnapshot(id: number): Promise<CommandOutcome> { return this.#mutate(Opcode.Delete, [tlv.u32(TlvTag.SnapshotId, id)]); }
  clearGraph(head: HeadId): Promise<CommandOutcome> { return this.#mutate(Opcode.ClearGraph, [tlv.u8(TlvTag.Head, head - 1)]); }
  runDemo(head: HeadId): Promise<CommandOutcome> { return this.#mutate(Opcode.RunDemo, [tlv.u8(TlvTag.Head, head - 1)]); }
  resetBatch(): Promise<CommandOutcome> { return this.#mutate(Opcode.ResetBatch); }
  repairStorage(): Promise<CommandOutcome> { return this.#mutate(Opcode.RepairStorage); }
  confirm(token: bigint): Promise<CommandOutcome> {
    const plan = this.#confirmationPlans.get(token) ?? {
      successScope: 0,
      recoveryScope: RefreshScope.State | RefreshScope.Catalog | RefreshScope.ReferenceTrace,
    };
    this.#confirmationPlans.delete(token);
    return this.#mutate(Opcode.ConfirmPending, [tlv.u64(TlvTag.Token, token)], plan);
  }
  cancel(token: bigint): Promise<CommandOutcome> {
    const pending = this.#confirmationPlans.get(token);
    this.#confirmationPlans.delete(token);
    return this.#mutate(Opcode.CancelPending, [tlv.u64(TlvTag.Token, token)], {
      successScope: 0,
      recoveryScope: RefreshScope.State,
      project: pending?.cancelProjection
        ? (state) => { pending.cancelProjection!(state); return true; }
        : undefined,
    });
  }

  async saveSettings(settings: SettingDescriptor[]): Promise<CommandOutcome> {
    if (settings.length !== SETTING_COUNT || settings.some((setting, index) => setting.id !== index)) throw new Error(`All ${SETTING_COUNT} settings are required in device order.`);
    if (!this.#state) throw new Error("Reconnect before changing controller state.");
    const changed = settings.filter((setting) => this.#state?.settings[setting.id]?.value !== setting.value);
    // A direct API caller can still request an explicit full reapply when no
    // value changed; the UI normally disables that path. PATCH_CONFIG always
    // carries at least one delta record.
    const patchSupported = this.#state.capabilities.configPatch && changed.length > 0;
    const sentSettings = patchSupported ? changed : settings;
    const values = sentSettings.map((setting) => {
      const writer = new BinaryWriter().u8(setting.id);
      if (setting.kind === "boolean") writer.u8(0x01).u8(setting.value ? 1 : 0);
      else writer.u8(0x06).f32(Number(setting.value));
      return tlv.raw(TlvTag.SettingValue, writer.build());
    });
    const acceptedSettings = settings.map((setting) => ({ ...setting }));
    return this.#mutate(patchSupported ? Opcode.PatchConfig : Opcode.SetConfig, values, {
      successScope: 0,
      recoveryScope: RefreshScope.State | RefreshScope.Config,
      projectWhenQueued: true,
      completionAfterPersistencePending: true,
      project: (state, outcome) => {
        state.settings = acceptedSettings.map((setting) => ({ ...setting }));
        state.settingsApplyPending = outcome.code === "queued";
        state.statePersistencePending = outcome.code === "persistence_pending";
        if (outcome.code === "persistence_pending") state.statePersistenceFailed = false;
        return true;
      },
    });
  }

  onEvent(listener: (event: RemoteEvent) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #handleEvent(message: ProtocolMessage): void {
    try {
      const tlvs = decodeTlvs(message.payload);
      if (message.opcode === Opcode.OperationCompleted) {
        const version = this.#outcomeVersion(tlvs);
        const eventSession = version.session;
        const requestId = readU16(required(tlvs, TlvTag.CompletedRequestId, 2));
        if (requestId === 0) throw new Error("Invalid completed request ID");
        const status = readU8(required(tlvs, TlvTag.ResultStatus, 1)) as StatusCode;
        // A completion can update revision/generation before the matching
        // targeted read finishes. Invalidate that read before touching shared
        // state so it cannot combine old catalog data with the new outcome.
        this.#refreshInvalidationGeneration += 1;
        if (!this.#state || this.#state.sessionId !== eventSession) {
          this.#pendingCompletions.clear();
          this.#confirmationPlans.clear();
          this.#scheduleRefresh(RefreshScope.State | RefreshScope.InferFromState);
          return;
        }
        const pending = this.#pendingCompletions.get(requestId);
        this.#pendingCompletions.delete(requestId);
        if (!pending) {
          // Completion IDs are meaningful only while this connection tracks a
          // queued request. Never let an old/wrapped ID advance the visible
          // lifecycle or patch command-specific state.
          this.#scheduleRefresh(RefreshScope.State | RefreshScope.InferFromState);
          return;
        }
        const code = STATUS_CODES[status];
        const stale = this.#isOlderVersion(version);
        if (stale) {
          // This result predates state the client has already accepted. Keep
          // the visible request non-terminal until its affected scope has been
          // reconciled; never emit an historical OK as a current SAVED result.
          const recovery = this.#startMutationRecovery(pending.plan.recoveryScope);
          this.#emit({ type: "operation_verifying", requestId, recovery });
          return;
        }
        const outcome = this.#decodeOutcome(
          status,
          tlvs,
          requestId,
          true,
          code !== undefined && acceptedOutcome(code),
        );
        let scope = RefreshScope.State | RefreshScope.Config | RefreshScope.Catalog | ALL_TRACE_SCOPE;
        if (outcome.confirmation) {
          // A queued SAVE may discover a full catalog only when it finally
          // reaches persistence. Carry its original projection through the
          // new confirmation token so CONFIRM_PENDING can apply the delta.
          this.#confirmationPlans.set(outcome.confirmation.token, pending.plan);
          scope = 0;
        } else if (["ok", "persistence_pending", "queued"].includes(outcome.code)) {
          if (outcome.code === "ok" && pending.plan.completionAfterPersistencePending) {
            this.#state.statePersistencePending = false;
            this.#state.statePersistenceFailed = false;
          }
          const projectionComplete = outcome.code !== "queued"
            ? this.#applyProjection(pending.plan, outcome, tlvs)
            : true;
          scope = projectionComplete
            ? pending.plan.successScope
            : (pending.plan.projectionFallbackScope ?? pending.plan.recoveryScope);
          if (outcome.code === "persistence_pending") scope |= RefreshScope.State;
          if (outcome.code === "queued") this.#pendingCompletions.set(requestId, pending);
        } else {
          scope = pending.plan.recoveryScope;
        }
        this.#emit({ type: "operation", requestId, outcome });
        this.#scheduleRefresh(scope);
      } else if (message.opcode === Opcode.StateChanged) {
        const eventSession = readU32(required(tlvs, TlvTag.Session, 4));
        const eventRevision = readU32(required(tlvs, TlvTag.Revision, 4));
        const eventGeneration = readU32(required(tlvs, TlvTag.Generation, 4));
        if (!this.#state || this.#state.sessionId !== eventSession || eventRevision > this.#state.revision || eventGeneration > this.#state.generation) {
          this.#refreshInvalidationGeneration += 1;
          this.#scheduleRefresh(RefreshScope.State | RefreshScope.InferFromState);
        }
      } else if (message.opcode === Opcode.SnapshotsChanged) {
        this.#refreshInvalidationGeneration += 1;
        this.#scheduleRefresh(RefreshScope.State | RefreshScope.Catalog | RefreshScope.ReferenceTrace);
      } else {
        this.#refreshInvalidationGeneration += 1;
        this.#scheduleRefresh(RefreshScope.State | RefreshScope.InferFromState);
      }
    } catch (error) {
      console.warn("Ignored malformed controller event", error);
    }
  }

  #applyLiveSample(value?: Uint8Array): void {
    if (!value) return;
    try {
      const live = parseLiveRecord(value);
      this.#liveActivityGeneration += 1;
      this.#latestLiveRecord = value.slice();
      if (!this.#state || this.#stateConnectionEpoch !== this.#connectionEpoch) return;
      const wasActive = { 1: this.#headActive(1), 2: this.#headActive(2) };
      for (const head of [1, 2] as const) {
        this.#state.heads[head] = applyLiveHeadRecord(this.#state.heads[head], live.heads[head], live.calibration);
        if (!wasActive[head] && this.#headActive(head)) this.#deferredRefreshScope |= headTraceScope(head);
      }
      this.#emit({ type: "state", device: structuredClone(this.#state) });
      if ((wasActive[1] || wasActive[2]) && !this.#headsActive()) this.#flushDeferredRefreshIfIdle();
    } catch (error) {
      console.warn("Ignored malformed BLE live notification", error);
    }
  }

  #startMutationRecovery(scope: number): Promise<void> {
    this.#pendingMutationRecoveryScope |= scope;
    if (this.#mutationRecovery) return this.#mutationRecovery;
    if (this.#refreshTimer !== undefined) window.clearTimeout(this.#refreshTimer);
    this.#refreshTimer = undefined;
    const epoch = this.#connectionEpoch;
    const work = (async () => {
      let retryCount = 0;
      for (;;) {
        if (this.#syncing) await this.#syncing;
        if (this.#refreshing) await this.#refreshing.catch(() => undefined);
        this.#assertConnectionEpoch(epoch);
        const requestedScope = this.#pendingMutationRecoveryScope;
        this.#pendingMutationRecoveryScope = 0;
        const invalidationGeneration = this.#refreshInvalidationGeneration;
        try {
          await this.#performScopedRefresh(
            requestedScope,
            epoch,
            0,
            invalidationGeneration,
            false,
          );
          retryCount = 0;
        } catch (error) {
          this.#pendingMutationRecoveryScope |= requestedScope;
          if (error instanceof RefreshSupersededError) continue;
          if (epoch !== this.#connectionEpoch || !this.#transport.connected) throw error;
          retryCount += 1;
          const delayMs = Math.min(5_000, 200 * 2 ** Math.min(retryCount - 1, 5));
          await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
          continue;
        }
        if (!this.#pendingMutationRecoveryScope) return;
      }
    })();
    const tracked = work.finally(() => {
      if (this.#mutationRecovery !== tracked) return;
      this.#mutationRecovery = undefined;
      if (this.#pendingRefreshScope) this.#armRefreshTimer();
      else this.#flushDeferredRefreshIfIdle();
    });
    this.#mutationRecovery = tracked;
    return tracked;
  }

  #scheduleRefresh(scope: number): void {
    if (!scope) return;
    if (scope & RefreshScope.InferFromState) scope |= RefreshScope.State;
    const heavyScope = scope & HEAVY_REFRESH_SCOPE;
    if (this.#headsActive() && heavyScope) {
      this.#deferredRefreshScope |= heavyScope;
      scope &= ~HEAVY_REFRESH_SCOPE;
    }
    this.#pendingRefreshScope |= scope;
    if (!this.#syncing && !this.#refreshing && !this.#mutationRecovery) this.#armRefreshTimer();
  }

  #armRefreshTimer(delayMs = 80): void {
    if (this.#refreshTimer !== undefined || !this.#pendingRefreshScope || this.#mutationRecovery) return;
    this.#refreshTimer = window.setTimeout(() => {
      this.#refreshTimer = undefined;
      if (this.#syncing || this.#refreshing || this.#mutationRecovery) return;
      const scope = this.#pendingRefreshScope;
      this.#pendingRefreshScope = 0;
      const epoch = this.#connectionEpoch;
      const tracked = this.#performScopedRefresh(scope, epoch);
      this.#refreshing = tracked;
      let retryDelayMs = 80;
      void tracked
        .then(() => { this.#refreshRetryCount = 0; })
        .catch((error) => {
          if (error instanceof RefreshSupersededError) {
            this.#pendingRefreshScope |= scope;
            return;
          }
          console.warn("Controller targeted refresh failed", error);
          if (epoch !== this.#connectionEpoch || !this.#transport.connected) return;
          this.#refreshRetryCount += 1;
          retryDelayMs = Math.min(5_000, 200 * 2 ** Math.min(this.#refreshRetryCount - 1, 5));
          this.#pendingRefreshScope |= scope;
        })
        .finally(() => {
          if (this.#refreshing !== tracked || epoch !== this.#connectionEpoch) return;
          this.#refreshing = undefined;
          if (this.#pendingRefreshScope) this.#armRefreshTimer(retryDelayMs);
          else this.#flushDeferredRefreshIfIdle();
        });
    }, delayMs);
  }

  #headsActive(): boolean {
    return Boolean(this.#state && ([1, 2] as const).some((head) =>
      this.#state!.heads[head].detectorActive || this.#state!.heads[head].demoActive));
  }

  #headActive(head: HeadId): boolean {
    return Boolean(this.#state && (this.#state.heads[head].detectorActive || this.#state.heads[head].demoActive));
  }

  #flushDeferredRefreshIfIdle(): void {
    if (!this.#deferredRefreshScope || !this.#transport.connected || this.#headsActive()) return;
    const scope = this.#deferredRefreshScope;
    this.#deferredRefreshScope = 0;
    this.#scheduleRefresh(scope);
  }

  #resetLiveConnectionBaselines(): void {
    this.#latestLiveRecord = undefined;
    if (!this.#state) return;
    for (const head of [1, 2] as const) {
      this.#state.heads[head] = {
        ...this.#state.heads[head],
        liveTraceEpoch: undefined,
        liveSampleIndex: undefined,
        liveTotalTicks: undefined,
        liveConnectionResetPending: true,
      };
    }
  }

  #liveReplayBase(parsed: HeadState, cached: HeadState): HeadState {
    return {
      ...parsed,
      trace: cached.trace,
      reference: cached.reference,
      liveSampleIndex: cached.liveSampleIndex,
      liveTraceEpoch: cached.liveTraceEpoch,
      liveTotalTicks: cached.liveTotalTicks,
      liveConnectionResetPending: cached.liveConnectionResetPending,
      wireTraceKind: cached.wireTraceKind,
    };
  }

  #beginConnectionEpoch(connected: boolean): void {
    this.#connectionEpoch += 1;
    this.#stateConnectionEpoch = undefined;
    if (this.#refreshTimer !== undefined) window.clearTimeout(this.#refreshTimer);
    this.#refreshTimer = undefined;
    this.#syncing = undefined;
    this.#refreshing = undefined;
    this.#mutationRecovery = undefined;
    this.#pendingMutationRecoveryScope = 0;
    this.#pendingRefreshScope = 0;
    this.#deferredRefreshScope = 0;
    this.#refreshRetryCount = 0;
    this.#pendingCompletions.clear();
    this.#confirmationPlans.clear();
    this.#latestLiveRecord = undefined;
    if (connected) this.#resetLiveConnectionBaselines();
  }

  #assertConnectionEpoch(epoch: number): void {
    if (epoch !== this.#connectionEpoch || !this.#transport.connected) {
      throw new Error("Controller connection changed during synchronization.");
    }
  }

  #assertScopedRefreshCurrent(epoch: number, invalidationGeneration: number): void {
    this.#assertConnectionEpoch(epoch);
    if (invalidationGeneration !== this.#refreshInvalidationGeneration) {
      throw new RefreshSupersededError("Controller refresh was superseded by newer activity.");
    }
  }

  #emit(event: RemoteEvent): void {
    for (const listener of this.#listeners) listener(event);
  }
}

export function checkedName(name: string): string {
  const trimmed = name.trim();
  const encoded = new TextEncoder().encode(trimmed);
  if (!trimmed || encoded.length > 24 || encoded.some((byte) => byte < 0x20 || byte === 0x7f)) {
    throw new Error("Snapshot names must be 1-24 UTF-8 bytes without control characters.");
  }
  return trimmed;
}
